import { useMemo } from 'react';
import { Values } from '@lib/interfaces/form';
import {
  ActiveTabState,
  FilterState,
  ModalTableMultiselectStates,
} from './useModalTableMultiselectStates';
import useModalTableMultiselect from './useModalTableMultiselect';
import useModalTableMultiunselect from './useModalTableMultiunselect';
import { ModalTableMultiselectMode, TableTabs } from '../enums';

type SelectedOnlyStates = Pick<
  ModalTableMultiselectStates,
  | 'searchStateSelected'
  | 'paginationStateSelected'
  | 'orderingStateSelected'
  | 'filterStateSelected'
>;

interface UseModalTableModeManagerArgs {
  isOpen: boolean;
  mode: ModalTableMultiselectMode;
  selectedIds: string[];
  filterStateDefault: Values;
}

interface UseModalTableModeManagerReturn extends SelectedOnlyStates {
  activeTab: TableTabs;
  activeTabState?: ActiveTabState;
  clearFilter: () => void;
  filterState?: FilterState;
  idsForSelectedTabQuery: string[];
  isSelectedOnlyMode: boolean;
  isSelectedTabActive: boolean;
  orderingState?: ModalTableMultiselectStates['orderingState'];
  paginationState?: ModalTableMultiselectStates['paginationState'];
  searchState?: ModalTableMultiselectStates['searchState'];
  setSelectedIdsForQuery: (ids: string[]) => void;
}

export default function useModalTableModeManager({
  isOpen,
  mode,
  selectedIds,
  filterStateDefault,
}: UseModalTableModeManagerArgs): UseModalTableModeManagerReturn {
  const isSelectedOnlyMode = mode === ModalTableMultiselectMode.SelectedOnly;

  const multiselectHook = useModalTableMultiselect({
    isOpen: !isSelectedOnlyMode && isOpen,
    selected: selectedIds,
    filterStateDefault,
  });

  const multiunselectHook = useModalTableMultiunselect({
    isOpen: isSelectedOnlyMode && isOpen,
    selected: selectedIds,
    filterStateDefault,
  });

  const baseHook = isSelectedOnlyMode ? multiunselectHook : multiselectHook;

  const {
    searchStateSelected,
    paginationStateSelected,
    orderingStateSelected,
    filterStateSelected,
    idsForSelectedTabQuery,
    setSelectedIdsForQuery,
  } = baseHook;

  const activeTabState = !isSelectedOnlyMode
    ? multiselectHook.activeTabState
    : undefined;
  const searchState = !isSelectedOnlyMode
    ? multiselectHook.searchState
    : undefined;
  const paginationState = !isSelectedOnlyMode
    ? multiselectHook.paginationState
    : undefined;
  const orderingState = !isSelectedOnlyMode
    ? multiselectHook.orderingState
    : undefined;
  const filterState = !isSelectedOnlyMode
    ? multiselectHook.filterState
    : undefined;
  const [, setFilter] = multiselectHook.filterState;
  const [, setFilterSelected] = baseHook.filterStateSelected;

  // Reset must clear the filter values on BOTH tabs (All + Selected) since
  // the user-facing filter row is shared across them. Without resetting
  // `filterStateSelected` here, the Selected tab keeps stale filter values
  // after the Reset action.
  const clearFilter = () => {
    if (setFilter) {
      setFilter({ ...filterStateDefault });
    }
    if (setFilterSelected) {
      setFilterSelected({ ...filterStateDefault });
    }
  };

  const activeTab = activeTabState ? activeTabState[0] : TableTabs.Selected;

  const isSelectedTabActive = useMemo(() => {
    if (isSelectedOnlyMode) {
      return true;
    }
    return activeTab === TableTabs.Selected;
  }, [activeTab, isSelectedOnlyMode]);

  return {
    activeTab,
    activeTabState,
    clearFilter,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isSelectedOnlyMode,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    setSelectedIdsForQuery,
  };
}
